import { baseApi } from '@app/baseApi';

import type { NotificationConfig } from '@/types';

import {
  fromNotificationConfigResponse,
  toDeleteNotificationConfigPayload,
  toSaveNotificationConfigPayload,
} from './monitoring.mappers';

export interface SaveNotificationConfigRequest {
  tenantId: string;
  appId: string;
  environmentId: string;
  id?: number;
  emailAddresses: string;
  success: boolean;
  failure: boolean;
  partialSuccess: boolean;
}

export const monitoringApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get notification config (email monitoring) - matches monitoring-svc API
    // 404 = no config yet (expected) - treat as success with null instead of error
    getMonitoringConfig: builder.query<
      NotificationConfig | null,
      { tenantId: string; appId: string; environmentId: string }
    >({
      async queryFn(arg, _queryApi, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `notification/getconfig/${arg.tenantId}/${arg.appId}/${arg.environmentId}`,
          endpoint: 'monitor',
        });
        if (result.error && result.error.status === 404) {
          return { data: null };
        }
        if (result.error) return { error: result.error };
        return {
          data: fromNotificationConfigResponse(
            result.data as Parameters<typeof fromNotificationConfigResponse>[0]
          ),
        };
      },
      providesTags: (_result, _error, { tenantId, appId, environmentId }) => [
        { type: 'Monitoring', id: `${tenantId}-${appId}-${environmentId}` },
      ],
    }),

    // Create/Update notification config - matches dataProtect payload format
    saveMonitoringConfig: builder.mutation<void, SaveNotificationConfigRequest>({
      query: (data) => ({
        url: 'notification/configure',
        endpoint: 'monitor',
        method: 'POST',
        body: toSaveNotificationConfigPayload(data),
      }),
      invalidatesTags: (_result, _error, { tenantId, appId, environmentId }) => [
        { type: 'Monitoring', id: `${tenantId}-${appId}-${environmentId}` },
      ],
    }),

    // Delete notification config
    deleteMonitoringConfig: builder.mutation<
      void,
      { tenantId: string; appId: string; environmentId: string }
    >({
      query: (data) => ({
        url: 'notification/deleteconfig',
        endpoint: 'monitor',
        method: 'DELETE',
        body: toDeleteNotificationConfigPayload(data),
      }),
    }),

    // Get execution logs for workspace dashboard
    getWorkspaceExecutionLogs: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any[],
      {
        tenantId: string;
        appId: string;
        duration: string;
        state?: string;
        startTime?: string;
        endTime?: string;
      }
    >({
      query: ({ tenantId, appId, duration, state = 'all', startTime = '', endTime = '' }) => {
        const params = new URLSearchParams({
          duration,
          state,
          ...(startTime && { startTime }),
          ...(endTime && { endTime }),
        });
        return {
          url: `${tenantId}/logs/filtered/transformers/app/${appId}?${params.toString()}`,
          endpoint: 'transformer', // FIXED: Use transformer API, not main API
        };
      },
      providesTags: ['Job'],
    }),
  }),
});

export const {
  useGetMonitoringConfigQuery,
  useSaveMonitoringConfigMutation,
  useDeleteMonitoringConfigMutation,
  useGetWorkspaceExecutionLogsQuery,
} = monitoringApi;
